import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';

interface PricingFeature {
  name: string;
  // Inclure d'autres champs si nécessaire pour la logique interne, mais le parent filtrera
}

interface PricingPlanCardProps {
  planKey: 'teaser' | 'regular' | 'vip'; // Pour identifier le plan
  title: string;
  price: string;
  features: PricingFeature[]; // Les fonctionnalités spécifiques à ce plan
  isPopular?: boolean;
  ctaText: string;
  ctaVariant?: 'outline' | 'default' | 'secondary' | 'destructive' | 'ghost' | 'link';
  ctaClassName?: string; // Pour les styles spécifiques comme le gradient ou or
  badgeText?: string;
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'pink' | 'gold'; // Ajouter des variantes custom si nécessaire
  cardClassName?: string; // Pour le style de la carte (bordure, échelle)
  onCtaClick: () => void;
}

const PricingPlanCard: React.FC<PricingPlanCardProps> = ({
  planKey,
  title,
  price,
  features,
  isPopular = false,
  ctaText,
  ctaVariant = 'default',
  ctaClassName = '',
  badgeText,
  badgeVariant = 'default',
  cardClassName = '',
  onCtaClick,
}) => {
  const { t } = useTranslation();

  // Définir les couleurs spécifiques aux badges ici si nécessaire
  const getBadgeSpecificClass = () => {
    switch (badgeVariant) {
      case 'pink':
        return 'bg-pink-DEFAULT text-white';
      case 'gold':
         return 'bg-gold-DEFAULT text-black';
      // Ajouter d'autres variantes si la logique CSS/Tailwind le permet facilement
      default:
        return ''; // Utilise les styles par défaut de Shadcn/UI Badge
    }
  }
   const getBadgeContainerSpecificClass = () => {
    switch (badgeVariant) {
      case 'pink':
        return 'bg-pink-light/20 text-pink-dark'; // Exemple, ajuster selon le style désiré
      case 'gold':
         return 'bg-gold-DEFAULT text-black';
      default:
        // Ajuster la couleur de fond par défaut si nécessaire
        return 'bg-muted text-muted-foreground';
    }
  }


  return (
    <Card className={`relative overflow-hidden ${cardClassName}`}>
      {badgeText && (
         // Utilisation de Badge pour une meilleure sémantique et styling potentiel
         <Badge
           variant={badgeVariant === 'pink' || badgeVariant === 'gold' ? 'default' : badgeVariant} // Utilise variant Shadcn si possible
           className={`absolute top-0 right-0 rounded-none rounded-bl-lg px-4 py-1 ${getBadgeContainerSpecificClass()}`} // Positionnement et style spécifique
         >
           {badgeText}
         </Badge>
      )}
      <CardContent className="p-6 pt-12"> {/* Ajuster le padding top si le badge est présent */}
        <h3 className="text-2xl font-bold mb-2">{title}</h3>
        <p className="text-3xl font-bold mb-4">{price}</p>
        <ul className="space-y-3 mb-6">
          {/* Si une feature spéciale comme 'Tout le plan X' doit être ajoutée, elle peut être passée via les props */}
           {planKey === 'regular' && (
             <li className="flex items-start">
               <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
               <span>{t('pricing.all_teaser')}</span>
             </li>
           )}
           {planKey === 'vip' && (
             <li className="flex items-start">
               <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
               <span>{t('pricing.all_regular')}</span>
             </li>
           )}
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span>{feature.name}</span>
            </li>
          ))}
        </ul>
        <Button
          className={`w-full ${ctaClassName}`}
          variant={ctaVariant}
          onClick={onCtaClick}
        >
          {ctaText}
        </Button>
      </CardContent>
    </Card>
  );
};

export default PricingPlanCard; 